import { FC, useState, ReactNode } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

interface AutocompleteOption {
  id: string;
  label: string;
}

interface GenericAutocompleteProps<T extends AutocompleteOption> {
  options: T[];
  inputValue: T | null;
  setValue: (value: T) => void;
  placeholder: string;
  width?: string;
  startDecorator?: ReactNode;
  disabled?: boolean;
}

const GenericAutocomplete: FC<
  GenericAutocompleteProps<AutocompleteOption>
> = ({
  options,
  inputValue,
  setValue,
  placeholder,
  width = "100%",
  startDecorator,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (optionId: string) => {
    const option = options.find((o) => o.id === optionId);
    if (option) {
      setValue(option);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 flex h-9 items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          )}
          style={{ width }}
        >
          <span className="flex items-center gap-2 overflow-hidden">
            {startDecorator}
            <span className={cn(!inputValue && "text-muted-foreground", "truncate")}>
              {inputValue ? inputValue.label : placeholder}
            </span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.label}
                  onSelect={() => handleSelect(option.id)}
                >
                  <span className="flex-1">{option.label}</span>
                  {inputValue?.id === option.id && (
                    <Check className="size-4 shrink-0" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default GenericAutocomplete;
